import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import AdminToolbar from '@/src/components/layout/AdminToolbar'
import { DataTable } from '@/src/components/manage/tables/DataTable'
import { listProjects, rollProjectShareId } from '@/src/api/project'
import { useTranslation } from '@/src/i18n/client'
import { EditAction } from '@/src/components/manage/actions/EditAction'
import { useAuth } from '@/src/components/AuthContextProvider'
import type { Project } from '@/src/types/project.zod'
import { getProjectColumns } from '@/src/components/manage/projects/projectColumns'
import { ViewAction } from '@/src/components/manage/actions/ViewAction'
import { CreateAction } from '@/src/components/manage/actions/CreateAction'

export const Route = createFileRoute('/_manage/manage/admin/projects/')({
  component: AdminProjectsPage,
})

function AdminProjectsPage() {
  const { t } = useTranslation('manage')
  const queryClient = useQueryClient()
  const { isAdmin } = useAuth()

  const { data, isLoading, error } = useQuery({
    queryKey: ['project', 'listAll'],
    queryFn: () => listProjects({ data: { all: true } }),
  })

  const projects = data ?? []

  const rollShareIdMutation = useMutation({
    mutationFn: (projectId: string) => rollProjectShareId({ data: { projectId } }),
  })

  const handleRotateShareId = async (projectId: string) => {
    await rollShareIdMutation.mutateAsync(projectId)
    await queryClient.invalidateQueries({ queryKey: ['project', 'listAll'] })
  }

  const columns = getProjectColumns({
    t,
    canRotateShareId: () => isAdmin,
    onRotateShareId: handleRotateShareId,
  })

  return (
    <div className="bg-background text-foreground">
      <AdminToolbar>
        <AdminToolbar.Left>
          <CreateAction to="/manage/projects/new" label={t('table.createProject')} />
        </AdminToolbar.Left>
      </AdminToolbar>
      <div className="mt-4">
        <DataTable<Project>
          data={projects}
          columns={columns}
          isLoading={isLoading}
          error={error instanceof Error ? error.message : undefined}
          emptyMessage={t('table.noProjects')}
          actions={(project) => (
            <>
              <ViewAction
                to={`/manage/projects/${project.id}`}
              />
              <EditAction
                to={`/manage/projects/${project.id}/edit`}
              />
            </>
          )}
        />
      </div>
    </div>
  )
}
